<template>
  <v-dialog
    v-model="dialog"
    max-width="860"
    :fullscreen="$vuetify.breakpoint.smAndDown"
  >
    <v-card class="an rounded-lg">
      <!-- header -->
      <div class="an__head">
        <div class="an__head-text">
          <h2 class="an__title">Rendimiento</h2>
          <p class="an__sub">{{ propertyLabel || "Propiedad" }}</p>
        </div>
        <button type="button" class="an__close" @click="dialog = false" aria-label="Cerrar">
          <hero-icon :icon="XMarkIcon" :size="18" />
        </button>
      </div>

      <div class="an__divider"></div>

      <div class="an__body">
        <!-- range selector -->
        <div class="an__ranges">
          <button
            v-for="r in ranges"
            :key="r.days"
            type="button"
            class="an__range"
            :class="{ 'an__range--on': range === r.days }"
            @click="setRange(r.days)"
          >
            {{ r.label }}
          </button>
        </div>

        <!-- KPIs (lifetime totals) -->
        <div class="an__kpis">
          <div class="an__kpi">
            <div class="an__kpi-num">{{ data.totalVisits || 0 }}</div>
            <div class="an__kpi-lbl">Visitas</div>
          </div>
          <div class="an__kpi">
            <div class="an__kpi-num">{{ data.totalInquiries || 0 }}</div>
            <div class="an__kpi-lbl">Consultas</div>
          </div>
          <div class="an__kpi">
            <div class="an__kpi-num">{{ data.totalSearchAppearances || 0 }}</div>
            <div class="an__kpi-lbl">En búsquedas</div>
          </div>
        </div>

        <div v-if="loading" class="an__loading">
          <v-progress-circular indeterminate color="accent" size="30" width="2" />
        </div>

        <template v-else>
          <!-- searches vs visits over time (how the property is performing) -->
          <section class="an__section">
            <div class="an__label">Búsquedas vs Visitas · últimos {{ range }} días</div>
            <apexchart type="area" height="230" :options="perfOpts" :series="performanceSeries" />
          </section>

          <div class="an__grid">
            <!-- source breakdown -->
            <section class="an__section">
              <div class="an__label">Fuentes</div>
              <apexchart
                v-if="sourceSeries.length"
                type="donut"
                height="240"
                :options="donutOpts"
                :series="sourceSeries"
              />
              <div v-else class="an__empty">Sin visitas en este período</div>
            </section>

            <!-- top referrers -->
            <section class="an__section">
              <div class="an__label">Llegaron desde</div>
              <div v-if="(data.topReferrers || []).length" class="an__refs">
                <div v-for="r in data.topReferrers" :key="r.property ? r.property.id : Math.random()" class="an__ref">
                  <div class="an__ref-body">
                    <div class="an__ref-type">{{ typeLabel(r.property) }}</div>
                    <div class="an__ref-addr">{{ r.property ? r.property.fullAddress : "—" }}</div>
                  </div>
                  <div class="an__ref-count">{{ r.count }}</div>
                </div>
              </div>
              <div v-else class="an__empty">Ninguna recomendación derivó visitas aún</div>
            </section>
          </div>

          <!-- inquiries over time -->
          <section class="an__section">
            <div class="an__label">Consultas por día · últimos {{ range }} días</div>
            <apexchart type="area" height="200" :options="inquiryOpts" :series="inquiriesSeries" />
          </section>
        </template>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { PropertyAnalytics } from "@/graphql/queries/analytics";
import HeroIcon from "@/components/shared/HeroIcon";
import XMarkIcon from "heroicons/24/outline/x-mark.svg";
import { areaOptions, dualAreaOptions, donutOptions, fillDaily, localDateKey, BROWSER_TZ, ACCENT } from "@/utils/charts";

export default {
  components: { HeroIcon },

  props: {
    propertyId: { type: [Number, String], required: true },
    propertyLabel: { type: String, default: "" }
  },

  data: () => ({
    XMarkIcon,
    dialog: false,
    loading: false,
    range: 30,
    ranges: [
      { days: 7, label: "7 días" },
      { days: 30, label: "30 días" },
      { days: 90, label: "90 días" }
    ],
    data: {}
  }),

  computed: {
    rangeDates() {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - (this.range - 1));
      return { from: localDateKey(from), to: localDateKey(to) };
    },
    visitsSeries() {
      return [{ name: "Visitas", data: fillDaily(this.data.daily, this.range) }];
    },
    performanceSeries() {
      return [
        { name: "Visitas", data: fillDaily(this.data.daily, this.range) },
        { name: "Búsquedas", data: fillDaily(this.data.dailySearches, this.range) }
      ];
    },
    inquiriesSeries() {
      return [{ name: "Consultas", data: fillDaily(this.data.dailyInquiries, this.range) }];
    },
    sourceKeys() {
      return (this.data.sources || []).map(s => s.label);
    },
    sourceSeries() {
      return (this.data.sources || []).map(s => s.count);
    },
    areaOpts() {
      return areaOptions(ACCENT);
    },
    perfOpts() {
      return dualAreaOptions();
    },
    inquiryOpts() {
      return areaOptions("#2c6e49");
    },
    donutOpts() {
      return donutOptions(this.sourceKeys);
    }
  },

  methods: {
    open() {
      this.dialog = true;
      this.fetch();
    },

    setRange(days) {
      this.range = days;
      this.fetch();
    },

    typeLabel(property) {
      if (!property || !property.propertyType) return "Propiedad";
      return this.$t("property.type." + property.propertyType.toLowerCase());
    },

    fetch() {
      this.loading = true;
      this.$apollo
        .query({
          query: PropertyAnalytics,
          fetchPolicy: "no-cache",
          variables: {
            id: this.propertyId,
            from: this.rangeDates.from,
            to: this.rangeDates.to,
            timeZone: BROWSER_TZ
          }
        })
        .then(res => {
          this.data = res.data.propertyAnalytics || {};
          this.loading = false;
          // nudge ApexCharts to remeasure once the dialog is fully laid out
          this.$nextTick(() => window.dispatchEvent(new Event("resize")));
        })
        .catch(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style scoped>
.an { background: #fff; }

.an__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 14px;
}
.an__title { font-size: 18px; font-weight: 700; color: #1a1814; margin: 0; line-height: 1.2; }
.an__sub { font-size: 13px; color: #756f64; margin: 4px 0 0; }
.an__close {
  flex: none; display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 8px; background: transparent; border: none;
  cursor: pointer; color: #756f64; transition: background 0.15s, color 0.15s;
}
.an__close:hover { background: #f1ece3; color: #1a1814; }
.an__divider { height: 1px; background: #ece7de; }

.an__body { padding: 18px 24px 24px; }

/* range pills */
.an__ranges { display: flex; gap: 8px; margin-bottom: 18px; }
.an__range {
  border: 1px solid #e3ddd2; background: #fff; border-radius: 999px;
  padding: 6px 14px; font-size: 12.5px; font-weight: 600; color: #56504a; cursor: pointer;
  transition: all 0.15s ease;
}
.an__range:hover { border-color: #dcd4c6; }
.an__range--on { background: #1a1814; color: #fff; border-color: #1a1814; }

/* KPIs */
.an__kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 20px; }
.an__kpi {
  border: 1px solid #ece7de; border-radius: 12px; padding: 14px 16px; background: #faf8f4;
}
.an__kpi-num { font-size: 26px; font-weight: 700; color: #1a1814; line-height: 1; }
.an__kpi-lbl { font-size: 12.5px; color: #756f64; margin-top: 5px; }

.an__section { margin-bottom: 18px; min-width: 0; }
.an >>> .apexcharts-canvas { max-width: 100%; }
.an__label {
  font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: #a39a8c;
  font-weight: 600; margin-bottom: 8px;
}
.an__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 720px) { .an__grid { grid-template-columns: 1fr; } }

.an__loading { display: flex; justify-content: center; padding: 48px 0; }
.an__empty {
  border: 1px dashed #dcd4c6; border-radius: 12px; padding: 28px; text-align: center;
  color: #a39a8c; font-size: 13px;
}

/* referrers */
.an__refs { display: flex; flex-direction: column; gap: 8px; }
.an__ref {
  display: flex; align-items: center; gap: 12px; justify-content: space-between;
  border: 1px solid #ece7de; border-radius: 10px; padding: 10px 14px;
}
.an__ref-body { min-width: 0; }
.an__ref-type { font-size: 13.5px; font-weight: 600; color: #1a1814; }
.an__ref-addr {
  font-size: 12px; color: #756f64; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.an__ref-count {
  flex: none; min-width: 30px; text-align: center; font-weight: 700; color: #b52118; font-size: 15px;
}
</style>
